import React, { useEffect, useState } from "react";
import {
  Button, Card, CardBody, CardHeader, Col, Container, Row,
  Modal, ModalHeader, ModalBody, FormGroup, Label, Input,
} from "reactstrap";
import Breadcrumbs from "../../components/Common/Breadcrumb";
import Pagination from "../../components/Pagination/Pagination";
import {
  confirmDeleteCropCategory,
  createOrUpdateCropCategory,
  getCropCategories as fetchCropCategories,
} from "./services/CropCategoryService";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import "primereact/resources/themes/saga-blue/theme.css";
import "primereact/resources/primereact.min.css";
import "primeicons/primeicons.css";
import { ConfirmDialog } from "primereact/confirmdialog";

const CropCategoryListTables = () => {
  document.title = "Crop Categories | Valitan IMS";

  const [cropCategories, setCropCategories] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);

  const [cropCategoryName, setCropCategoryName] = useState("");
  const [cropCategoryCode, setCropCategoryCode] = useState("");

  const [errors, setErrors] = useState({
    cropCategoryName: "",
    cropCategoryCode: "",
  });

  const [editingCropCategoryId, setEditingCropCategoryId] = useState(null);

  const [paginationInfo, setPaginationInfo] = useState({
    totalElements: 0,
    totalPages: 0,
    pageNumber: 0,
    pageSize: 10,
    sort: {},
  });

  useEffect(() => {
    loadCropCategories(0, paginationInfo.pageSize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paginationInfo.pageSize]);

  const loadCropCategories = async (page = 0, size = 10) => {
    const { cropCategories, paginationInfo } = await fetchCropCategories(page, size);
    setCropCategories(cropCategories || []);
    setPaginationInfo(
      paginationInfo || {
        totalElements: 0,
        totalPages: 0,
        pageNumber: 0,
        pageSize: size,
        sort: {},
      }
    );
  };

  const handleNextPage = () => {
    if (paginationInfo.pageNumber < paginationInfo.totalPages - 1) {
      loadCropCategories(paginationInfo.pageNumber + 1, paginationInfo.pageSize);
    }
  };

  const handlePreviousPage = () => {
    if (paginationInfo.pageNumber > 0) {
      loadCropCategories(paginationInfo.pageNumber - 1, paginationInfo.pageSize);
    }
  };

  const openModalForEdit = async (category) => {
    setEditingCropCategoryId(category.id);
    setCropCategoryName(category.name ?? "");
    setCropCategoryCode(category.code ?? "");
    setModalOpen(true);
  };

  const toggleModal = () => {
    setModalOpen((prev) => !prev);
    if (!modalOpen) {
      // reset fields when opening modal for Add
      setCropCategoryName("");
      setCropCategoryCode("");
      setErrors({ cropCategoryName: "", cropCategoryCode: "" });
      setEditingCropCategoryId(null);
    }
  };

  const startIndex = paginationInfo.pageNumber * paginationInfo.pageSize + 1;

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <Breadcrumbs title="Crop Categories Management" breadcrumbItem="Crop Categories" />
          <Row>
            <Col lg={12}>
              <Card>
                <CardHeader>
                  <h4 className="card-title mb-0">
                    <Button
                      color="primary"
                      className="add-btn float-end"
                      id="TooltipTop"
                      onClick={toggleModal}
                    >
                      <i className="ri-add-line align-bottom me-1"></i> Add
                    </Button>
                  </h4>
                </CardHeader>

                <CardBody>
                  <div>
                    <Row className="g-4 mb-3">
                      <Col className="col-sm">
                        <div className="d-flex justify-content-sm-end">
                          <div className="search-box ms-2">
                            <input
                              type="text"
                              className="form-control search"
                              placeholder="Search..."
                            />
                          </div>
                        </div>
                      </Col>
                    </Row>

                    <div className="table-responsive table-card mt-3 mb-1">
                      <table className="table align-middle table-nowrap" id="cropCategoryTable">
                        <thead className="table-light">
                          <tr>
                            <th>#</th>
                            <th>Name</th>
                            <th>Code</th>
                            <th>Action</th>
                          </tr>
                        </thead>
                        <tbody>
                          {cropCategories.length > 0 ? (
                            cropCategories.map((cropCategory, index) => (
                              <tr key={cropCategory.id}>
                                <td>{startIndex + index}</td>
                                <td>{cropCategory.name}</td>
                                <td>{cropCategory.code}</td>
                                <td>
                                  <div className="btn-group" role="group">
                                    <button
                                      type="button"
                                      className="btn btn-primary btn-sm"
                                      onClick={() => openModalForEdit(cropCategory)}
                                    >
                                      <i className="ri-edit-box-line"></i>
                                    </button>
                                    <button
                                      type="button"
                                      onClick={() =>
                                        confirmDeleteCropCategory(cropCategory.id, loadCropCategories)
                                      }
                                      className="btn btn-danger btn-sm"
                                    >
                                      <i className="ri-delete-bin-line"></i>
                                    </button>
                                  </div>
                                </td>
                              </tr>
                            ))
                          ) : (
                            <tr>
                              <td colSpan="4" style={{ textAlign: "center" }}>
                                No records found
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>

                    <Pagination
                      paginationInfo={paginationInfo}
                      handleNextPage={handleNextPage}
                      handlePreviousPage={handlePreviousPage}
                      onPageChange={(pageNumber, pageSize) =>
                        loadCropCategories(pageNumber, pageSize)
                      }
                      onPageSizeChange={(newPageSize) => {
                        setPaginationInfo((prev) => ({
                          ...prev,
                          pageSize: newPageSize,
                          pageNumber: 0,
                        }));
                      }}
                    />
                  </div>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>

      {/* ConfirmDialog */}
      <ConfirmDialog />

      {/* Toasts */}
      <ToastContainer />

      <Modal isOpen={modalOpen} toggle={toggleModal} backdrop={"static"}>
        <ModalHeader toggle={toggleModal}>
          {editingCropCategoryId ? "Edit Crop Category" : "Add Crop Category"}
        </ModalHeader>
        <ModalBody>
          <form
            onSubmit={(evt) =>
              createOrUpdateCropCategory(
                evt,
                cropCategoryName,
                cropCategoryCode,
                editingCropCategoryId,
                loadCropCategories,
                toggleModal,
                setErrors
              )
            }
          >
            <FormGroup>
              <Label for="cropCategoryName">Name</Label>
              <Input
                type="text"
                id="cropCategoryName"
                placeholder="Enter Name"
                value={cropCategoryName}
                onChange={(e) => setCropCategoryName(e.target.value)}
                invalid={!!errors.cropCategoryName}
              />
              {errors.cropCategoryName && (
                <div className="invalid-feedback">{errors.cropCategoryName}</div>
              )}
            </FormGroup>

            <FormGroup>
              <Label for="cropCategoryCode">Code</Label>
              <Input
                type="text"
                id="cropCategoryCode"
                value={cropCategoryCode}
                onChange={(e) => setCropCategoryCode(e.target.value)}
                invalid={!!errors.cropCategoryCode}
              />
              {errors.cropCategoryCode && (
                <div className="invalid-feedback">{errors.cropCategoryCode}</div>
              )}
            </FormGroup>

            <Button type="submit" color="primary" className="float-end">
              {editingCropCategoryId ? "Update" : "Save"}
            </Button>
          </form>
        </ModalBody>
      </Modal>
    </React.Fragment>
  );
};

export default CropCategoryListTables;
